import { useCallback, useMemo, useState } from "react";
import {
    Card,
    CardContent,
    Typography,
    Box,
    Divider,
    useTheme,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Chip,
    CircularProgress,
    Alert,
} from "@mui/material";
import { FullEvent } from "../shared/FullEvent";
import { getHmiLabelForCreatorPrimary } from "../constants/languages";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TranslateIcon from '@mui/icons-material/Translate';
import EventsFormDialog from "../components/EventsFormDialog";
import CustomAlert from "../components/CustomAlert";
import EventResultCard from "../components/EventResultCard";
import MergeIcon from '@mui/icons-material/Merge';
import { useNavigate } from "react-router-dom";
import { useUnsavedChanges } from "../context/UnsavedChangesContext";
import WebIcon from '@mui/icons-material/Web';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { normalizeTranslationReviewMessage } from "../constants/translationReview";

type TranslationStatusPayload = {
    status?: string;
    message?: string;
    translated_languages?: string[];
    updated_at?: string | null;
};

function TranslationStatusPanel({
    title,
    statusPayload,
}: {
    title: string;
    statusPayload?: TranslationStatusPayload;
}) {
    const theme = useTheme();
    const status = (statusPayload?.status || "idle").toLowerCase();
    const langs = statusPayload?.translated_languages ?? [];
    const message = (statusPayload?.message || "").trim();

    const isBusy = status === "pending" || status === "running";
    const isDone = status === "done";
    const isError = status === "error";
    const isIdleNoInfo = status === "idle" && !message;

    if (!statusPayload || isIdleNoInfo) {
        return null;
    }

    const isIdleInfo = status === "idle" && Boolean(message);

    let chipColor: "default" | "primary" | "success" | "warning" | "error" = "default";
    let chipLabel = status;
    if (isBusy) {
        chipColor = "primary";
        chipLabel = status === "pending" ? "Queued" : "Translating…";
    } else if (isDone) {
        chipColor = "success";
        chipLabel = "Complete";
    } else if (isError) {
        chipColor = "error";
        chipLabel = "Failed";
    } else if (isIdleInfo) {
        chipColor = "warning";
        chipLabel = "Not run";
    }

    const alertSeverity = isError ? "error" : isDone ? "success" : isIdleInfo ? "warning" : "info";

    return (
        <Alert
            severity={alertSeverity}
            icon={
                isBusy ? (
                    <CircularProgress size={22} thickness={4} />
                ) : isDone ? (
                    <CheckCircleIcon fontSize="inherit" />
                ) : isError ? (
                    <ErrorOutlineIcon fontSize="inherit" />
                ) : isIdleInfo ? (
                    <ErrorOutlineIcon fontSize="inherit" />
                ) : (
                    <TranslateIcon fontSize="inherit" />
                )
            }
            sx={{
                alignItems: "center",
                "& .MuiAlert-message": { width: "100%" },
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: theme.palette.text.primary }}>
                    {title}
                </Typography>
                <Chip size="small" label={chipLabel} color={chipColor} variant={isDone ? "filled" : "outlined"} />
            </Stack>
            {isDone && langs.length > 0 && (
                <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
                    Languages updated: {langs.map((c) => c.toUpperCase()).join(", ")}
                </Typography>
            )}
            {isDone && langs.length === 0 && !message && (
                <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
                    No new translations were written (source unchanged or nothing to translate).
                </Typography>
            )}
            {message && (
                <Typography variant="caption" sx={{ mt: 0.5, display: "block", color: theme.palette.text.secondary }}>
                    {message}
                </Typography>
            )}
            {isBusy && (
                <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
                    EN / IT / CZ texts are generated in the background. This usually takes a few seconds.
                </Typography>
            )}
        </Alert>
    );
}

interface EventResultProps {
    fullEventRising?: FullEvent;
    fullEventResolving?: FullEvent;
    risingEventCode?: number;
    resolvingEventCode?: number;
    risingEventId?: number;
    resolvingEventId?: number;
    risingTranslationStatus?: any;
    resolvingTranslationStatus?: any;
    /** Shown when backend queued automatic translations (creator_mode). */
    risingTranslationReview?: { message: string } | null;
    resolvingTranslationReview?: { message: string } | null;
    /** Quick action for starting a new creator flow immediately. */
    onCreateAnotherEvent?: () => void;
}

export default function EventResultPage({
    fullEventRising,
    fullEventResolving,
    risingEventCode,
    resolvingEventCode,
    risingEventId,
    resolvingEventId,
    risingTranslationStatus,
    resolvingTranslationStatus,
    risingTranslationReview,
    resolvingTranslationReview,
    onCreateAnotherEvent,
}: EventResultProps) {
    const navigate = useNavigate();
    const { setHasUnsavedChanges } = useUnsavedChanges();
    const [copiedDeclaration, setCopiedDeclaration] = useState(false);
    const [copiedRising, setCopiedRising] = useState(false);
    const [copiedResolving, setCopiedResolving] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertSeverity, setAlertSeverity] = useState<"error" | "success">("error");

    const [editEventId, setEditEventId] = useState<number | null>(null);
    const handleNavigateToEvent = useCallback((id: number) => {
        setEditEventId(id);
    }, []);
    const presetCounterparts = useMemo(
        () => ({
            rising: risingEventId != null && risingEventId > 0 ? risingEventId : null,
            resolving: resolvingEventId != null && resolvingEventId > 0 ? resolvingEventId : null,
        }),
        [risingEventId, resolvingEventId]
    );

    const declaration = "EventQueueFb : EventQueue_FB;";
    const snippetRising = `EventQueueFb(EventCode := ${risingEventCode}, Par1 := NDC8.LayoutPosition.Segment, Par2 := ${fullEventRising?.attributes?.parameters?.[1]?.parameter_contents || 0}, Par3 := ${fullEventRising?.attributes?.parameters?.[2]?.parameter_contents || 0}, Par4 := ${fullEventRising?.attributes?.parameters?.[3]?.parameter_contents || 0}, Estop := TRUE, ShowPanel := TRUE, ActivInManual := FALSE, GenerateBlackbox := TRUE);`;
    const snippetResolving = `EventQueueFb(EventCode := ${resolvingEventCode}, Par1 := NDC8.LayoutPosition.Segment, Par2 := ${fullEventResolving?.attributes?.parameters?.[1]?.parameter_contents || 0}, Par3 := ${fullEventResolving?.attributes?.parameters?.[2]?.parameter_contents || 0}, Par4 := ${fullEventResolving?.attributes?.parameters?.[3]?.parameter_contents || 0}, Estop := TRUE, ShowPanel := TRUE, ActivInManual := FALSE, GenerateBlackbox := TRUE);`;

    const copyDeclarationToClipboard = async () => {
        await navigator.clipboard.writeText(declaration);
        setCopiedDeclaration(true);
        setTimeout(() => setCopiedDeclaration(false), 1800);
    };

    const copyRisingToClipboard = async () => {
        await navigator.clipboard.writeText(snippetRising);
        setCopiedRising(true);
        setTimeout(() => setCopiedRising(false), 1800);
    };

    const copyResolvingToClipboard = async () => {
        await navigator.clipboard.writeText(snippetResolving);
        setCopiedResolving(true);
        setTimeout(() => setCopiedResolving(false), 1800);
    };

    const theme = useTheme();
    const hmiEventTextRising = getHmiLabelForCreatorPrimary(
        fullEventRising?.event_descriptions,
        fullEventRising?.attributes?.creator_source_language
    );
    const hmiEventTextResolving = getHmiLabelForCreatorPrimary(
        fullEventResolving?.event_descriptions,
        fullEventResolving?.attributes?.creator_source_language
    );

    const showTranslationReviewBanner =
        Boolean(risingTranslationReview?.message) || Boolean(resolvingTranslationReview?.message);

    return (
        <Box maxWidth={800} margin="0 auto" display="flex" flexDirection="column" gap={4}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon sx={{ color: theme.palette.success.main, mr: 2 }} />
                <Typography sx={{ color: theme.palette.text.primary }} variant="h5" fontWeight="bold">
                    Event creation successful
                </Typography>
            </Box>

            {showTranslationReviewBanner && (
                <Alert severity="warning" variant="filled" icon={false} sx={{ py: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                        Important: Review and correct translations
                    </Typography>
                    {risingTranslationReview?.message && risingEventCode ? (
                        <Typography variant="body2" sx={{ mb: resolvingTranslationReview?.message ? 1.5 : 0 }}>
                            <strong>Rising ({risingEventCode}):</strong>{" "}
                            {normalizeTranslationReviewMessage(risingTranslationReview.message)}
                        </Typography>
                    ) : null}
                    {resolvingTranslationReview?.message && resolvingEventCode ? (
                        <Typography variant="body2">
                            <strong>Resolving ({resolvingEventCode}):</strong>{" "}
                            {normalizeTranslationReviewMessage(resolvingTranslationReview.message)}
                        </Typography>
                    ) : null}
                    <Typography variant="body2" sx={{ mt: 1.5, opacity: 0.95 }}>
                        Additional languages are generated in the background — please read through all texts in
                        the event editor before you approve or export the event.
                    </Typography>
                </Alert>
            )}

            {/* Rising event */}
            {risingEventCode != 0 && risingEventCode != null &&
                <>
                <TranslationStatusPanel
                    title="Rising event — translations"
                    statusPayload={risingTranslationStatus}
                />
                <EventResultCard
                    title="Rising event"
                    eventCode={risingEventCode}
                    eventId={risingEventId!}

                    onEdit={(id) => setEditEventId(id)}

                    hmiEventText={hmiEventTextRising}

                    declaration={declaration}
                    snippet={snippetRising}

                    copiedDeclaration={copiedDeclaration}
                    copiedSnippet={copiedRising}

                    onCopyDeclaration={copyDeclarationToClipboard}
                    onCopySnippet={copyRisingToClipboard}
                />
                </>
            }

            {/* Resolving event */}
            {resolvingEventCode != 0 && resolvingEventCode != null &&
                <>
                <TranslationStatusPanel
                    title="Resolving event — translations"
                    statusPayload={resolvingTranslationStatus}
                />
                <EventResultCard
                    title="Resolving event"
                    eventCode={resolvingEventCode}
                    eventId={resolvingEventId!}

                    onEdit={(id) => setEditEventId(id)}

                    hmiEventText={hmiEventTextResolving}

                    declaration={declaration}
                    snippet={snippetResolving}

                    copiedDeclaration={copiedDeclaration}
                    copiedSnippet={copiedResolving}

                    onCopyDeclaration={copyDeclarationToClipboard}
                    onCopySnippet={copyResolvingToClipboard}
                />
                </>
            }

            {/* Next Step */}
            <Card elevation={3}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        <Typography sx={{ color: theme.palette.text.primary }}>Possible next steps</Typography>
                    </Typography>
                    <Divider sx={{b: 1}}/>
                    <List disablePadding>
                        <ListItem sx={{pl: 0, pb: 0}}>
                            <ListItemButton
                                component="a"
                                href="https://dev.azure.com/EK-AUTOMATION/BC%20Standardsoftware"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <ListItemIcon>
                                    <MergeIcon />
                                </ListItemIcon>
                                <ListItemText primary="Create Pull Request" />
                            </ListItemButton>
                        </ListItem>
                        <ListItem sx={{pl: 0, pt: 0}}>
                            {onCreateAnotherEvent && (
                                <ListItemButton onClick={onCreateAnotherEvent}>
                                    <ListItemIcon>
                                        <AddCircleOutlineIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Create another event" />
                                </ListItemButton>
                            )}
                        </ListItem>
                        <ListItem sx={{pl: 0, pt: 0}}>
                            <ListItemButton
                                onClick={() => {
                                    setHasUnsavedChanges(false);
                                    navigate("/", { replace: false });
                                }}
                            >
                                <ListItemIcon>
                                    <WebIcon />
                                </ListItemIcon>
                                <ListItemText primary="Browse Events" />
                            </ListItemButton>

                        </ListItem>
                    </List>



                </CardContent>
            </Card>
            <EventsFormDialog
                open={!!editEventId}
                eventId={editEventId}
                createNewEvent={false}
                onNavigateToEvent={handleNavigateToEvent}
                presetCounterparts={presetCounterparts}
                onClose={() => {
                    setEditEventId(null);
                }}
                onSaved={({ eventCode, approved }) => {
                    if (approved)
                        setAlertMessage(`Successfully approved event ${eventCode}!`);
                    else
                        setAlertMessage(`Successfully saved ${eventCode ?? 'event'}.`);
                    setAlertSeverity("success");
                    setAlertOpen(true);
                }}
            />
            <CustomAlert
                open={alertOpen}
                onClose={() => setAlertOpen(false)}
                message={alertMessage}
                severity={alertSeverity}
            />
        </Box>
    );
}